import { Box, Button, Chip, Divider, Sheet, Textarea, Typography } from "@mui/joy";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isAdminRole, useRole } from "../Contexts/RoleContext";
import { Util } from "../Helpers/Util";

interface Comment {
	service_report_comments_id: string;
	comment_text: string;
	date_entered: string;
	created_by: string;
}

interface Picture {
	service_report_pictures_id: string;
	url: string;
}

interface ElevatorLine {
	service_report_elevators_id: string;
	report_type: string;
	line_comments?: string;
	serial_number?: string;
	elevator_type?: string;
	elevator_description?: string;
	pictures: Picture[];
	comments: Comment[];
}

interface BuildingLine {
	service_report_buildings_id: string;
	buildings_id: string;
	building_name?: string;
	elevators: ElevatorLine[];
	comments: Comment[];
}

interface ViewRow {
	service_report_views_id: string;
	viewed_at: string;
	viewed_by: string;
}

const primaryActionSx = {
	alignSelf: "flex-start",
	fontWeight: 600,
	bgcolor: "#1565C0",
	color: "#fff",
	border: "1px solid #0D47A1",
	"&:hover": { bgcolor: "#0D47A1" },
	"&.Mui-disabled, &:disabled": {
		bgcolor: "#E3F2FD",
		color: "#1565C0",
		borderColor: "#90CAF9",
		opacity: 1,
	},
} as const;

interface ServiceReportDetail {
	service_reports_id: string;
	report_date: string;
	entered_by: string;
	device_saved_at: string;
	server_received_at: string;
	finished_at?: string | null;
	finished_by?: string;
	comments: Comment[];
	buildings: BuildingLine[];
	views: ViewRow[];
}

function MetaRow(props: { label: string; value: React.ReactNode }) {
	return (
		<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
			<Typography level="body-sm" sx={{ fontWeight: 600, minWidth: 120, color: "text.primary" }}>
				{props.label}
			</Typography>
			<Typography level="body-sm" sx={{ color: "text.primary", flex: 1 }}>
				{props.value}
			</Typography>
		</Box>
	);
}

function Section(props: { title: string; subtitle?: string; children: React.ReactNode; accent?: "primary" | "neutral" | "warning" }) {
	const accent = props.accent ?? "neutral";
	const bar =
		accent === "primary" ? "primary.500" : accent === "warning" ? "warning.400" : "neutral.400";
	return (
		<Sheet
			variant="outlined"
			sx={{
				borderRadius: "sm",
				overflow: "hidden",
				bgcolor: "background.surface",
				boxShadow: "sm",
			}}>
			<Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider", borderLeft: "4px solid", borderLeftColor: bar, bgcolor: "background.level1" }}>
				<Typography level="title-md" sx={{ color: "text.primary" }}>
					{props.title}
				</Typography>
				{props.subtitle ? (
					<Typography level="body-xs" sx={{ color: "text.secondary", mt: 0.25 }}>
						{props.subtitle}
					</Typography>
				) : null}
			</Box>
			<Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>{props.children}</Box>
		</Sheet>
	);
}

function CommentList(props: { scopeLabel: string; comments: Comment[]; onAdd: (text: string) => Promise<void> }) {
	const [text, setText] = React.useState("");
	const [saving, setSaving] = React.useState(false);
	const [error, setError] = React.useState("");

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<Typography level="title-sm" sx={{ color: "text.secondary" }}>
				{props.scopeLabel}
			</Typography>
			{(props.comments ?? []).length === 0 ? (
				<Typography level="body-sm" sx={{ color: "text.tertiary", fontStyle: "italic" }}>
					No comments yet.
				</Typography>
			) : (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
					{(props.comments ?? []).map((comment) => (
						<Box
							key={comment.service_report_comments_id}
							sx={{
								p: 1.25,
								borderRadius: "sm",
								border: "1px solid",
								borderColor: "neutral.outlinedBorder",
								bgcolor: "background.body",
							}}>
							<Typography level="body-xs" sx={{ fontWeight: 600, color: "text.secondary" }}>
								{comment.created_by} · {Util.Formatter(comment.date_entered, "DT")}
							</Typography>
							<Typography level="body-sm" sx={{ whiteSpace: "pre-wrap", mt: 0.5, color: "text.primary" }}>
								{comment.comment_text}
							</Typography>
						</Box>
					))}
				</Box>
			)}
			<Box
				sx={{
					mt: 0.5,
					p: 1.25,
					borderRadius: "sm",
					border: "1px solid",
					borderColor: "primary.outlinedBorder",
					bgcolor: "#fff",
					display: "flex",
					flexDirection: "column",
					gap: 1,
				}}>
				<Typography level="body-xs" sx={{ fontWeight: 600, color: "text.primary" }}>
					Add portal comment
				</Typography>
				<Textarea
					minRows={2}
					value={text}
					placeholder="Write a comment…"
					onChange={(event) => setText(event.target.value)}
					sx={{ bgcolor: "#fff", borderColor: "neutral.outlinedBorder" }}
				/>
				{error && (
					<Typography level="body-sm" color="danger">
						{error}
					</Typography>
				)}
				<Button
					size="sm"
					variant="solid"
					sx={primaryActionSx}
					disabled={saving || text.trim() === ""}
					onClick={() => {
						setSaving(true);
						setError("");
						props
							.onAdd(text.trim())
							.then(() => setText(""))
							.catch((ex) => setError(ex.message || "Could not add comment."))
							.finally(() => setSaving(false));
					}}>
					Post comment
				</Button>
			</Box>
		</Box>
	);
}

export default function ServiceReportDetailPage() {
	const { id } = useParams();
	const navigate = useNavigate();
	const { effectiveRole } = useRole();
	const [report, setReport] = React.useState<ServiceReportDetail | null>(null);
	const [error, setError] = React.useState("");
	const [loading, setLoading] = React.useState(true);
	const [marking, setMarking] = React.useState(false);
	const staff = isAdminRole(effectiveRole);

	const load = React.useCallback((recordView = false) => {
		if (!staff || !id) return Promise.resolve();
		setLoading(true);
		return fetch("ServiceReport/GetServiceReport", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ serviceReportsId: id, recordView }),
		})
			.then((response) => response.json())
			.then((body) => {
				if (body && body.error) {
					setError(body.error);
					setReport(null);
				} else {
					setError("");
					setReport(body);
				}
				setLoading(false);
			})
			.catch(() => {
				setError("Could not load this service report.");
				setLoading(false);
			});
	}, [id, staff]);

	React.useEffect(() => {
		load(true);
	}, [load]);

	const addComment = (text: string, target: { serviceReportsId?: string; serviceReportBuildingsId?: string; serviceReportElevatorsId?: string }) => {
		return fetch("ServiceReport/AddComment", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ commentText: text, ...target }),
		})
			.then((response) => response.json())
			.then((body) => {
				if (body && body.error) throw new Error(body.error);
				return load();
			});
	};

	const markFinished = () => {
		if (!report) return;
		setMarking(true);
		setError("");
		fetch("ServiceReport/MarkFinished", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ serviceReportsId: report.service_reports_id }),
		})
			.then((response) => response.json())
			.then((body) => {
				if (body && body.error) throw new Error(body.error);
				return load();
			})
			.catch((ex) => setError(ex.message || "Could not mark this report finished."))
			.finally(() => setMarking(false));
	};

	if (!staff) return <Typography>Service reports are not available for this login.</Typography>;
	if (loading && !report) return <CircularProgress size={28} />;
	if (!report) return <Typography>{error || "Service report was not found."}</Typography>;

	const finished = !!report.finished_at;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pb: 3, maxWidth: 960 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					<Typography level="h4" sx={{ m: 0 }}>
						Service Report
					</Typography>
					<Chip size="sm" variant="soft" color={finished ? "success" : "warning"}>
						{finished ? "Finished" : "Pending"}
					</Chip>
				</Box>
				<Box sx={{ display: "flex", gap: 1 }}>
					{!finished && (
						<Button disabled={marking} onClick={markFinished} sx={primaryActionSx}>
							Mark Finished
						</Button>
					)}
					<Button
						variant="outlined"
						onClick={() => navigate("/servicereports")}
						sx={{ bgcolor: "#fff", borderColor: "neutral.outlinedBorder", color: "text.primary", fontWeight: 600 }}>
						Back
					</Button>
				</Box>
			</Box>

			<Section title="Summary" accent={finished ? "primary" : "warning"}>
				<MetaRow label="Report date" value={Util.Formatter(report.report_date, "D")} />
				<MetaRow label="Entered by" value={report.entered_by} />
				<MetaRow label="Saved on phone" value={Util.Formatter(report.device_saved_at, "DT")} />
				<MetaRow label="Received" value={Util.Formatter(report.server_received_at, "DT")} />
				{finished ? (
					<MetaRow label="Finished" value={`${report.finished_by} · ${Util.Formatter(report.finished_at, "DT")}`} />
				) : null}
				{error && (
					<Typography level="body-sm" color="danger">
						{error}
					</Typography>
				)}
			</Section>

			<Section title="View history" subtitle="Logged when someone opens this report">
				<Table size="small" sx={{ bgcolor: "background.body", border: "1px solid", borderColor: "divider", borderRadius: "sm" }}>
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 600 }}>Who</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>When</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{(report.views ?? []).length === 0 && (
							<TableRow>
								<TableCell colSpan={2}>No views yet.</TableCell>
							</TableRow>
						)}
						{(report.views ?? []).map((view) => (
							<TableRow key={view.service_report_views_id}>
								<TableCell>{view.viewed_by}</TableCell>
								<TableCell>{Util.Formatter(view.viewed_at, "DT")}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Section>

			<Section title="Report" subtitle="Whole-report portal comments">
				<CommentList
					scopeLabel="Comments on this report"
					comments={report.comments ?? []}
					onAdd={(text) => addComment(text, { serviceReportsId: report.service_reports_id })}
				/>
			</Section>

			{(report.buildings ?? []).map((building) => (
				<Section key={building.service_report_buildings_id} title={building.building_name || "Building"} accent="primary">
					<CommentList
						scopeLabel="Comments on this building"
						comments={building.comments ?? []}
						onAdd={(text) => addComment(text, { serviceReportBuildingsId: building.service_report_buildings_id })}
					/>
					{(building.elevators ?? []).length > 0 && <Divider />}
					{(building.elevators ?? []).map((elevator) => {
						const serial = elevator.serial_number ? `Serial #${elevator.serial_number}` : "Elevator";
						const detail = [elevator.elevator_description, elevator.elevator_type].filter(Boolean).join(" · ");
						const types = (elevator.report_type ?? "")
							.split(",")
							.map((t) => t.trim())
							.filter(Boolean);
						return (
							<Box
								key={elevator.service_report_elevators_id}
								sx={{
									p: 1.5,
									borderRadius: "sm",
									border: "1px solid",
									borderColor: "primary.outlinedBorder",
									borderLeft: "4px solid",
									borderLeftColor: "primary.500",
									bgcolor: "background.body",
									display: "flex",
									flexDirection: "column",
									gap: 1.25,
								}}>
								<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
									<Typography level="title-sm" sx={{ color: "text.primary" }}>
										{serial}
										{detail ? ` · ${detail}` : ""}
									</Typography>
									{types.map((type) => (
										<Chip key={type} size="sm" variant="soft" color="primary">
											{type}
										</Chip>
									))}
								</Box>
								{elevator.line_comments ? (
									<Box sx={{ p: 1.25, borderRadius: "sm", bgcolor: "background.level1", border: "1px solid", borderColor: "divider" }}>
										<Typography level="body-xs" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5 }}>
											Field notes (from mobile)
										</Typography>
										<Typography level="body-sm" sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}>
											{elevator.line_comments}
										</Typography>
									</Box>
								) : null}
								{(elevator.pictures ?? []).length > 0 && (
									<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
										{elevator.pictures.map((picture) => (
											<a
												key={picture.service_report_pictures_id}
												href={picture.url}
												target="_blank"
												rel="noreferrer"
												style={{ borderRadius: 4, overflow: "hidden", border: "1px solid #ccc" }}>
												<img src={picture.url} alt="" style={{ width: 140, height: 140, objectFit: "cover", display: "block" }} />
											</a>
										))}
									</Box>
								)}
								<CommentList
									scopeLabel="Comments on this elevator"
									comments={elevator.comments ?? []}
									onAdd={(text) => addComment(text, { serviceReportElevatorsId: elevator.service_report_elevators_id })}
								/>
							</Box>
						);
					})}
				</Section>
			))}
		</Box>
	);
}
